import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import AuthImage from "../components/app/AuthImage";
import FavoriteButton from "../components/app/FavoriteButton";
import { Heart, MapPin, Star, Sparkles, Search } from "lucide-react";
import { SPECIALTY_LABEL } from "./FindCoaches";

export default function FavoritesPage() {
  const { lang } = useI18n();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setList(data);
    } catch (_) { setList([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const removeOne = (id, isActive) => {
    if (!isActive) setList((p) => p.filter((c) => c.id !== id));
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-up" style={{ opacity: 0 }} data-testid="favorites-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono mb-1">// {lang === "bg" ? "Любими" : "Saved"}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
              <Heart className="w-7 h-7 text-pitch fill-pitch" />
              {lang === "bg" ? "Любими треньори" : "Favorite coaches"}
            </h1>
            <p className="text-white/45 text-sm mt-1 font-mono">
              {lang === "bg"
                ? "Бързо ще ги намериш тук — и ще ги резервираш с един клик."
                : "Quick access to coaches you’ve saved. Book them in one click."}
            </p>
          </div>
          <Link to="/find-coaches" className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="favorites-discover">
            <Search className="w-3.5 h-3.5" /> {lang === "bg" ? "Открий още" : "Discover more"}
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="pitch-loader"><div className="ball" /></div></div>
        ) : list.length === 0 ? (
          <div className="card-sharp p-12 text-center" data-testid="favorites-empty">
            <Heart className="w-10 h-10 text-pitch/40 mx-auto mb-3" />
            <div className="font-oswald uppercase text-xl tracking-tight mb-2">
              {lang === "bg" ? "Все още нямаш любими" : "No saved coaches yet"}
            </div>
            <div className="text-white/40 text-sm font-mono mb-5">
              {lang === "bg"
                ? "Натисни сърцето върху всеки треньор, който ти харесва."
                : "Tap the heart on any coach to save them here."}
            </div>
            <Link to="/find-coaches" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-xs">
              <Search className="w-4 h-4" /> {lang === "bg" ? "Открий треньори" : "Find coaches"}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((c, i) => (
              <div key={c.id} className="animate-fade-up" style={{ animationDelay: `${i * 35}ms`, opacity: 0 }}>
                <FavCard c={c} lang={lang} onChange={(a) => removeOne(c.id, a)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function FavCard({ c, lang, onChange }) {
  return (
    <div className="card-coach p-6 relative group" data-testid={`fav-card-${c.id}`}>
      <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
        {c.is_pro && (
          <span className="chip-badge gold"><Sparkles className="w-3 h-3" /> PRO</span>
        )}
        <FavoriteButton coachId={c.id} onChange={onChange} />
      </div>
      <Link to={`/coach/${c.id}`} className="block">
        <div className="flex items-start gap-5 pr-20">
          <div className="w-20 h-20 flex-shrink-0 relative">
            {c.photo_url ? (
              <AuthImage src={c.photo_url} className="w-full h-full object-cover rounded-full border-2 border-pitch/40"
                fallback={<div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-2xl">{c.name?.[0]}</div>} />
            ) : (
              <div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-2xl">{c.name?.[0]}</div>
            )}
            {(c.badges || []).includes("verified") && (
              <span className="absolute -bottom-1 -right-1 w-6 h-6 bg-pitch text-white rounded-full flex items-center justify-center text-[11px] font-bold border-2 border-[#0A1628]">✓</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-oswald uppercase text-xl tracking-tight truncate">{c.name}</div>
            <div className="flex items-center gap-2 text-xs mt-1.5">
              <Star className="w-3.5 h-3.5 text-pitch fill-pitch" />
              <span className="font-mono text-white">{(c.rating || 0).toFixed(1)}</span>
              <span className="text-white/25">·</span>
              <span className="font-mono text-white/55">{c.review_count || 0} {lang === "bg" ? "оценки" : "reviews"}</span>
            </div>
            {c.city && (
              <div className="text-xs text-white/50 flex items-center gap-1 mt-1.5 font-mono">
                <MapPin className="w-3 h-3 text-pitch" /> {c.city}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {c.specialty && <span className="chip-badge">{SPECIALTY_LABEL(c.specialty, lang)}</span>}
        </div>
        <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
          <div>
            <div className="label-mono">{lang === "bg" ? "От" : "From"}</div>
            {c.hourly_rate != null ? (
              <div className="font-oswald text-2xl text-pitch text-glow-emerald leading-none mt-0.5">
                €{c.hourly_rate}<span className="text-xs text-white/40 ml-1">/{lang === "bg" ? "ч" : "h"}</span>
              </div>
            ) : <div className="font-oswald text-base text-white/40 leading-none mt-0.5">—</div>}
          </div>
          <span className="btn-primary px-4 py-2 text-xs flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            {lang === "bg" ? "Резервирай" : "Book"}
          </span>
        </div>
      </Link>
    </div>
  );
}
